import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { getDocuments, getVehicles, createDocument, uploadFile } from '../../lib/supabase'
import { format } from 'date-fns'
import { FileText, Filter, AlertCircle, ExternalLink, Plus, X, Upload } from 'lucide-react'

const documentTypeLabels = {
  registration: 'Registration',
  insurance: 'Insurance',
  title: 'Title',
  purchase_agreement: 'Purchase Agreement',
  service_record: 'Service Record',
  inspection: 'Inspection',
  photo: 'Photo',
  other: 'Other'
}

const DocumentCard = ({ document }) => {
  const hasUrl = !!document.document_url
  const Wrapper = hasUrl ? 'a' : 'div'
  const wrapperProps = hasUrl
    ? { href: document.document_url, target: '_blank', rel: 'noopener noreferrer' }
    : {}

  return (
    <Wrapper
      {...wrapperProps}
      className={`card p-4 flex items-center gap-4 transition-shadow group ${hasUrl ? 'hover:shadow-md cursor-pointer' : 'opacity-80'}`}
    >
      <div className="w-12 h-12 bg-rdc-cream rounded-lg flex items-center justify-center flex-shrink-0">
        <FileText size={24} className="text-rdc-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-black truncate group-hover:text-rdc-primary transition-colors">
          {document.title}
        </h3>
        <div className="flex items-center gap-3 text-sm text-rdc-taupe mt-1">
          <span className="capitalize">{documentTypeLabels[document.document_type] || document.document_type}</span>
          {document.vehicle && (
            <>
              <span>•</span>
              <span>{document.vehicle.year} {document.vehicle.make} {document.vehicle.model}</span>
            </>
          )}
        </div>
        {document.expiry_date && (
          <div className="text-xs text-rdc-taupe mt-1">
            Expires: {format(new Date(document.expiry_date), 'MMM d, yyyy')}
          </div>
        )}
      </div>
      {hasUrl ? (
        <ExternalLink size={18} className="text-rdc-warm-gray group-hover:text-rdc-primary transition-colors flex-shrink-0" />
      ) : (
        <span className="text-xs text-rdc-taupe flex-shrink-0">No file</span>
      )}
    </Wrapper>
  )
}

function UploadModal({ vehicles, user, onClose, onSuccess }) {
  const [file, setFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [formData, setFormData] = useState({
    title: '',
    document_type: 'other',
    vehicle_id: '',
    expiry_date: ''
  })

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0]
    if (selected) setFile(selected)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    const dropped = e.dataTransfer.files?.[0]
    if (dropped) setFile(dropped)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!file) {
      setError('Please select a file to upload')
      return
    }
    if (!formData.title.trim()) {
      setError('Please enter a document title')
      return
    }

    setUploading(true)
    try {
      const vehicleFolder = formData.vehicle_id || 'general'
      const filePath = `${user.id}/${vehicleFolder}/${Date.now()}-${file.name}`
      const { error: uploadError } = await uploadFile('documents', filePath, file)
      if (uploadError) throw uploadError

      // Store the storage path (not a URL) — getDocuments resolves signed URLs on load
      const { error: dbError } = await createDocument({
        owner_id: user.id,
        vehicle_id: formData.vehicle_id || null,
        document_type: formData.document_type,
        title: formData.title.trim(),
        document_url: filePath,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type,
        expiry_date: formData.expiry_date || null,
        uploaded_by: user.id
      })
      if (dbError) throw dbError

      onSuccess()
    } catch (err) {
      console.error('Upload error:', err)
      setError(err.message || 'Failed to upload document')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-rdc-warm-gray/30">
          <h2 className="font-display text-lg font-semibold text-black">Upload Document</h2>
          <button onClick={onClose} className="text-rdc-taupe hover:text-black">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* File drop zone */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            className="border-2 border-dashed border-rdc-warm-gray rounded-lg p-6 text-center"
          >
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileText size={24} className="text-rdc-primary" />
                <div className="text-left">
                  <p className="text-sm font-medium text-black">{file.name}</p>
                  <p className="text-xs text-rdc-taupe">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
                <button type="button" onClick={() => setFile(null)} className="text-red-500 hover:text-red-700 ml-2">
                  <X size={16} />
                </button>
              </div>
            ) : (
              <>
                <Upload size={28} className="text-rdc-taupe mx-auto mb-2" />
                <p className="text-sm text-rdc-taupe mb-2">Drag a file here or click to browse</p>
                <label className="btn-secondary cursor-pointer inline-block text-sm">
                  Choose File
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
              </>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-rdc-dark-gray mb-1">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="input-field"
              placeholder="e.g. 2026 Registration, Insurance Card"
              required
            />
          </div>

          {/* Document Type */}
          <div>
            <label className="block text-sm font-medium text-rdc-dark-gray mb-1">Document Type</label>
            <select
              value={formData.document_type}
              onChange={(e) => setFormData(prev => ({ ...prev, document_type: e.target.value }))}
              className="input-field"
            >
              {Object.entries(documentTypeLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {/* Vehicle (optional) */}
          {vehicles.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-1">Vehicle (optional)</label>
              <select
                value={formData.vehicle_id}
                onChange={(e) => setFormData(prev => ({ ...prev, vehicle_id: e.target.value }))}
                className="input-field"
              >
                <option value="">General (not vehicle-specific)</option>
                {vehicles.map(v => (
                  <option key={v.id} value={v.id}>
                    {v.year} {v.make} {v.model}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Expiry Date (optional) */}
          {['registration', 'insurance', 'inspection'].includes(formData.document_type) && (
            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-1">Expiry Date</label>
              <input
                type="date"
                value={formData.expiry_date}
                onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
                className="input-field"
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={uploading} className="btn-primary">
              {uploading ? 'Uploading...' : 'Upload Document'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function Documents() {
  const { user } = useAuth()
  const [documents, setDocuments] = useState([])
  const [vehicles, setVehicles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filterType, setFilterType] = useState('all')
  const [filterVehicle, setFilterVehicle] = useState('all')
  const [showUpload, setShowUpload] = useState(false)

  useEffect(() => {
    loadData()
  }, [user])

  const loadData = async () => {
    if (!user) return
    try {
      const [docsRes, vehiclesRes] = await Promise.all([
        getDocuments(user.id),
        getVehicles(user.id)
      ])

      if (docsRes.error) throw docsRes.error

      setDocuments(docsRes.data || [])
      setVehicles(vehiclesRes.data || [])
    } catch (err) {
      console.error('Error loading documents:', err)
      setError('Failed to load documents')
    } finally {
      setLoading(false)
    }
  }

  const handleUploadSuccess = () => {
    setShowUpload(false)
    loadData()
  }

  const filteredDocuments = documents.filter(doc => {
    if (filterType !== 'all' && doc.document_type !== filterType) return false
    if (filterVehicle !== 'all' && doc.vehicle_id !== filterVehicle) return false
    return true
  })

  const documentTypes = [...new Set(documents.map(d => d.document_type))]

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-rdc-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="page-title">Documents</h1>
          <p className="text-rdc-taupe mt-1">
            Registration, insurance, and vehicle documents
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Upload button (always visible when documents exist) */}
          {documents.length > 0 && (
            <button
              onClick={() => setShowUpload(true)}
              className="btn-primary inline-flex items-center gap-2 text-sm"
            >
              <Plus size={16} />
              Upload
            </button>
          )}

          {/* Filters */}
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-rdc-taupe" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="input-field py-2 text-sm"
            >
              <option value="all">All Types</option>
              {documentTypes.map(type => (
                <option key={type} value={type}>
                  {documentTypeLabels[type] || type}
                </option>
              ))}
            </select>
          </div>

          {vehicles.length > 1 && (
            <select
              value={filterVehicle}
              onChange={(e) => setFilterVehicle(e.target.value)}
              className="input-field py-2 text-sm"
            >
              <option value="all">All Vehicles</option>
              {vehicles.map(v => (
                <option key={v.id} value={v.id}>
                  {v.year} {v.make} {v.model}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {filteredDocuments.length === 0 ? (
        filterType !== 'all' || filterVehicle !== 'all' ? (
          <div className="card p-12 text-center">
            <FileText size={48} className="text-rdc-warm-gray mx-auto mb-4" />
            <h3 className="font-display text-lg font-semibold text-black mb-2">
              No documents match your filters
            </h3>
            <p className="text-rdc-taupe">
              Try adjusting your filter settings to see more documents.
            </p>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-rdc-cream flex items-center justify-center">
              <FileText size={28} className="text-rdc-taupe" strokeWidth={1.5} />
            </div>
            <h3 className="font-display text-lg font-semibold text-black mb-2">
              No documents uploaded
            </h3>
            <p className="text-rdc-taupe max-w-md mx-auto mb-6">
              Upload titles, insurance cards, registration, and receipts to build complete provenance.
            </p>
            <button
              onClick={() => setShowUpload(true)}
              className="btn-primary inline-flex items-center gap-2 mb-4"
            >
              <Plus size={18} />
              Upload Document
            </button>
            <p className="text-xs text-rdc-taupe mt-4">
              Documents are worth +15 points toward your Provenance Score
            </p>
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredDocuments.map(doc => (
            <DocumentCard key={doc.id} document={doc} />
          ))}
        </div>
      )}

      {showUpload && (
        <UploadModal
          vehicles={vehicles}
          user={user}
          onClose={() => setShowUpload(false)}
          onSuccess={handleUploadSuccess}
        />
      )}
    </div>
  )
}
